import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RightArrow from "../../../../Assets/Icons/RightArrow.png";
import LeftArrow from "../../../../Assets/Icons/LeftArrow.png";
import ExtraIcon from "../../../../Assets/Icons/ExtraIcon.png";
import Icons from "../../../../Assets/Icons";
import { fetchServiceItems } from "../../../../modules/NursignCare/ManageServices/ManageServiceThunk";
import Widgets from "../../../../widgets";
import strings from "../../../../utils/strings";

const MRList = ({ searchValue, onEntrySummaryChange }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    serviceItems,
    serviceItemsTotalPages,
    serviceItemsCurrentPage,
    serviceItemsTotalDocuments,
  } = useSelector((state) => state.manageService);

  const itemsPerPage = 10;

  const fetchList = (page = 1, search = searchValue) => {
    dispatch(fetchServiceItems({ page, limit: itemsPerPage, search }));
  };

  useEffect(() => {
    fetchList(1, searchValue);
  }, [searchValue]);

  const getLocaleString = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;

    let lang = i18n.language?.toLowerCase();
    if (lang === "vietnam") lang = "vi";
    if (lang === "vn") lang = "vi";

    return val[lang] || val.en || val.vi || "";
  };

  const truncateTableText = (value) => {
    const text = value || "-";
    return text.length > 15 ? `${text.slice(0, 15)}...` : text;
  };

  const filteredServiceItems = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return (serviceItems || [])
      .filter((item) => item.isActive === true)
      .filter((item) => {
        if (!normalizedSearch) return true;

        const serviceGroupTitle = getLocaleString(item.service?.title)?.toLowerCase();
        return serviceGroupTitle?.includes(normalizedSearch);
      })
      .sort((a, b) =>
        getLocaleString(a.service?.title).localeCompare(
          getLocaleString(b.service?.title),
          undefined,
          { sensitivity: "base" }
        )
      );
  }, [serviceItems, searchValue, i18n.language]);

  useEffect(() => {
    const normalizedSearch = searchValue.trim();

    onEntrySummaryChange?.({
      displayed: filteredServiceItems.length,
      total: normalizedSearch
        ? filteredServiceItems.length
        : serviceItemsTotalDocuments || filteredServiceItems.length,
    });
  }, [filteredServiceItems.length, serviceItemsTotalDocuments, onEntrySummaryChange, searchValue]);

  const renderPagination = () => {
    const pages = [];
    if (serviceItemsTotalPages <= 4) {
      for (let i = 1; i <= serviceItemsTotalPages; i++) pages.push(i);
    } else if (serviceItemsCurrentPage <= 2) {
      pages.push(1, 2, { type: "ellipsis" }, serviceItemsTotalPages);
    } else if (serviceItemsCurrentPage >= serviceItemsTotalPages - 1) {
      pages.push(1, { type: "ellipsis" }, serviceItemsTotalPages - 1, serviceItemsTotalPages);
    } else {
      pages.push(1, { type: "ellipsis" }, serviceItemsCurrentPage, { type: "ellipsis" }, serviceItemsTotalPages);
    }
    return pages;
  };

  return (
    <div className="nc-mr-secondary-box">
      <div className="nc-mr-price-box">
        <table className="nc-nr-price-layout">
          <thead className="nc-mr-thead">
            <tr>
              <th>{t("code")}</th>
              <th>{t("serviceGroup")}</th>
              <th>{t("subServiceGroup")}</th>
              <th>{t("serviceItem")}</th>
              <th>{t("id")}</th>
              <th>{t("createdUpdatedBy")}</th>
            </tr>
          </thead>
          <tbody className="nc-mr-tbody">
            {filteredServiceItems.map((item) => {
              const code = item.code || "-";
              const serviceGroup = getLocaleString(item.service?.title) || "-";
              const subServiceGroup = getLocaleString(item.subService?.title) || "-";
              const serviceItem = getLocaleString(item.title) || "-";
              const createdUpdatedBy = item.createdBy?.name || item.updatedBy?.name || "-";

              return (
                <tr
                  key={item._id}
                  className="nc-mr-table-row"
                  onClick={() => {
                    navigate(strings.navigation.serviceDetail, {
                      state: item,
                    });
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td title={code}>{truncateTableText(code)}</td>
                  <td title={serviceGroup}>{truncateTableText(serviceGroup)}</td>
                  <td title={subServiceGroup}>{truncateTableText(subServiceGroup)}</td>
                  <td title={serviceItem}>{truncateTableText(serviceItem)}</td>
                  <td>
                    <span
                      className="nc-copy-action"
                      title={t("copyId")}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(item._id);
                        Widgets.ToastPopup.success(t("idCopied"));
                      }}
                    >
                      {t("copy")}
                    </span>
                  </td>
                  <td title={createdUpdatedBy}>{truncateTableText(createdUpdatedBy)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredServiceItems.length === 0 && (
          <div className="no-record-container">
            <div className="no-record-icon">
              <img src={Icons.NoData} alt="No Data Found" />
            </div>
            <div className="no-record-text">{t("noRecordFound")}</div>
          </div>
        )}
        <div className="nc-mr-pagination-list">
          {serviceItemsTotalPages > 0 && (
            <>
              <button
                onClick={() => fetchList(serviceItemsCurrentPage - 1)}
                disabled={serviceItemsCurrentPage === 1}
                className="nc-mr-pagination-prev-list"
              >
                <img src={LeftArrow} alt="<" />
              </button>
              {renderPagination().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === "number" && fetchList(page)}
                  disabled={page === serviceItemsCurrentPage || page?.type === "ellipsis"}
                  className={`nc-mr-pagination-btn-list ${page === serviceItemsCurrentPage ? "active-list" : ""} ${page?.type === "ellipsis" ? "ellipse" : ""}`}
                >
                  {typeof page === "number" ? page : <img src={ExtraIcon} alt="..." className="department-extra-icon" />}
                </button>
              ))}
              <button
                onClick={() => fetchList(serviceItemsCurrentPage + 1)}
                disabled={serviceItemsCurrentPage === serviceItemsTotalPages}
                className="nc-mr-pagination-next-list"
              >
                <img src={RightArrow} alt=">" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MRList;
